"use client";

/**
 * RiskSummaryWidget — cash, margin, buying power, risk score.
 * Honest empties: missing fields show "—" / "n/a", never stub zeros as live risk.
 */
import React from "react";
import { Skeleton } from "antd";
import { SensitiveDataMask } from "@/shared/components/security/SensitiveDataMask";
import { formatCurrency } from "@/shared/utils/formatters";
import type { RiskSummaryValues } from "./lib/types";
import { WidgetChrome } from "./WidgetChrome";
import styles from "./WidgetChrome.module.css";
import local from "./RiskSummaryWidget.module.css";

export interface RiskSummaryWidgetProps {
  values: RiskSummaryValues;
  loading?: boolean;
  className?: string;
  maskValues?: boolean;
  label?: string;
  meta?: string;
  /** strip = horizontal metrics; stack = vertical (side rail) */
  layout?: "strip" | "stack";
}

function Cell({
  label,
  value,
  empty,
  loading,
}: {
  label: string;
  value: React.ReactNode;
  empty?: boolean;
  loading?: boolean;
}) {
  return (
    <div className={local.cell}>
      <span className={local.cellLabel}>{label}</span>
      {loading ? (
        <Skeleton.Input active size="small" style={{ width: 88, height: 22 }} />
      ) : (
        <span
          className={`${styles.mono} ${styles.tabular} ${local.cellValue} ${
            empty ? styles.valueEmpty : ""
          }`}
        >
          {value}
        </span>
      )}
    </div>
  );
}

function money(
  n: number | null | undefined,
  mask: boolean
): { node: React.ReactNode; empty: boolean } {
  if (n == null) return { node: "—", empty: true };
  const formatted = formatCurrency(n);
  return {
    node: mask ? <SensitiveDataMask value={formatted} /> : formatted,
    empty: false,
  };
}

export function RiskSummaryWidget({
  values,
  loading = false,
  className,
  maskValues = true,
  label = "Risk",
  meta = "Summary",
  layout = "strip",
}: RiskSummaryWidgetProps) {
  const cash = money(values.cash, maskValues);
  const invested = money(values.invested, maskValues);
  const buying = money(values.buyingPower, maskValues);
  const margin = money(values.marginUsed, maskValues);
  const riskScore =
    values.riskScore != null && Number.isFinite(values.riskScore)
      ? { node: String(values.riskScore), empty: false }
      : { node: "n/a", empty: true };

  return (
    <WidgetChrome
      label={label}
      meta={meta}
      aria-label="Risk summary"
      className={className}
    >
      <div
        className={`${local.grid} ${layout === "stack" ? local.gridStack : ""}`}
        role="group"
        aria-label="Risk metrics"
      >
        <Cell label="Cash" value={cash.node} empty={cash.empty} loading={loading} />
        <Cell
          label="Invested"
          value={invested.node}
          empty={invested.empty}
          loading={loading}
        />
        <Cell
          label="Buying power"
          value={
            values.buyingPower == null && !loading ? (
              <span className={styles.pending} title="Not provided by feed">
                n/a
              </span>
            ) : (
              buying.node
            )
          }
          empty={buying.empty}
          loading={loading}
        />
        <Cell
          label="Margin used"
          value={
            values.marginUsed == null && !loading ? (
              <span className={styles.pending} title="Not provided by feed">
                n/a
              </span>
            ) : (
              margin.node
            )
          }
          empty={margin.empty}
          loading={loading}
        />
        <Cell
          label="Risk score"
          value={riskScore.node}
          empty={riskScore.empty}
          loading={loading}
        />
      </div>
    </WidgetChrome>
  );
}
